import { useState, useEffect } from 'react';
import { useAuth } from 'context/AuthContext';
import { authService } from 'services/api';
import api from 'services/api';

// Mismas reglas que el backend (authController.actualizarPerfil).
const PHONE_REGEX = /^\d{7,10}$/;
const bytesUtf8 = (s) => new TextEncoder().encode(s).length;
// 'No phone provided' es el valor por defecto de la columna users.phone: se
// muestra como vacío. El teléfono solo se valida y se envía si el usuario lo
// cambia (hay teléfonos viejos con espacios o guiones que no deben bloquear
// el guardado del nombre o de la clave).
const telefonoGuardado = (p) => (p && p !== 'No phone provided' ? p : '');
const ROLE_LABEL = {
  super_admin: { text: '⚡ Super Admin', color: '#6c63ff' },
  // fondo explícito: `var(--accent)` + '22' no es un color válido y el chip salía sin fondo
  admin:       { text: '🏢 Administrador', color: 'var(--accent)', fondo: 'var(--accent-soft)' },
  user:        { text: '👤 Propietario', color: '#4ecdc4' },
};

// Lógica de la pantalla (estado, carga y acciones); la página solo pinta.
export default function useProfile() {
  const { user, setUser, refreshUser } = useAuth();
  const role = ROLE_LABEL[user?.role] || { text: user?.role, color: '#888' };
  const isOwner = user?.role === 'user';

  // El user del login llega sin teléfono; se refresca desde /me al abrir el
  // perfil para que el campo muestre el número ya guardado.
  useEffect(() => { refreshUser?.(); }, [refreshUser]);

  const [form, setForm] = useState({
    name:             user?.name      || '',
    last_name:        user?.last_name || '',
    phone:            telefonoGuardado(user?.phone),
    current_password: '',
    password:         '',
    confirm_password: '',
  });

  const [alert,   setAlert]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [dirty,   setDirty]   = useState(false); // el usuario ya tocó el formulario

  // El objeto `user` del contexto puede llegar incompleto en el primer render
  // (justo tras el login) y completarse cuando /me responde. Si el usuario aún
  // no ha editado nada, se re-siembra el formulario con los datos frescos —
  // así el teléfono ya guardado aparece en vez de un campo vacío.
  useEffect(() => {
    if (dirty) return;
    setForm(f => ({
      ...f,
      name:      user?.name      || '',
      last_name: user?.last_name || '',
      phone:     telefonoGuardado(user?.phone),
    }));
  }, [user, dirty]);

  // OTP para cambio de contraseña
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSent,     setOtpSent]     = useState(false);
  const [otpValue,    setOtpValue]    = useState('');
  const [otpSending,  setOtpSending]  = useState(false);
  const [otpLoading,  setOtpLoading]  = useState(false);
  const [otpError,    setOtpError]    = useState('');
  const [countdown,   setCountdown]   = useState(0);

  const sendOtp = async () => {
    setOtpSending(true); setOtpError('');
    try {
      await api.post('/auth/send-otp', { email: user?.email, motivo: 'cambio de contraseña' });
      setOtpSent(true);
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown(n => { if (n <= 1) { clearInterval(timer); return 0; } return n - 1; });
      }, 1000);
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Error al enviar el código');
    } finally { setOtpSending(false); }
  };

  const verifyOtp = async () => {
    if (otpValue.length !== 6) return setOtpError('El código debe tener 6 dígitos');
    setOtpLoading(true); setOtpError('');
    try {
      await api.post('/auth/verify-otp', { email: user?.email, otp: otpValue });
      setOtpVerified(true);
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Código incorrecto');
    } finally { setOtpLoading(false); }
  };

  const handleTogglePwd = () => {
    if (showPwd) {
      // Cancelar — resetear todo
      setShowPwd(false);
      setOtpVerified(false);
      setOtpSent(false);
      setOtpValue('');
      setOtpError('');
      setCountdown(0);
      setForm(f => ({ ...f, current_password: '', password: '', confirm_password: '' }));
    } else {
      setShowPwd(true);
    }
  };

  const set = (k, v) => { setDirty(true); setForm(f => ({ ...f, [k]: v })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);

    // noValidate en el <form>: estas revisiones reemplazan el globo del navegador.
    if (!form.name.trim())
      return setAlert({ type: 'danger', message: 'El nombre es requerido' });
    const telefonoCambio = form.phone.trim() !== telefonoGuardado(user?.phone).trim();
    if (telefonoCambio && form.phone.trim() && !PHONE_REGEX.test(form.phone.trim()))
      return setAlert({ type: 'danger', message: 'El teléfono no es válido — debe tener 7 (fijo) o 10 (celular) dígitos, sin espacios ni +57' });

    if (showPwd) {
      if (!form.current_password)
        return setAlert({ type: 'danger', message: 'Ingresa tu contraseña actual' });
      if (form.password.length < 8)
        return setAlert({ type: 'danger', message: 'La nueva contraseña debe tener mínimo 8 caracteres' });
      if (!form.password.trim())
        return setAlert({ type: 'danger', message: 'La contraseña no puede ser solo espacios' });
      if (bytesUtf8(form.password) > 72)
        return setAlert({ type: 'danger', message: 'La contraseña es demasiado larga (máximo 72 caracteres sin tildes ni ñ)' });
      if (form.password !== form.confirm_password)
        return setAlert({ type: 'danger', message: 'Las contraseñas no coinciden' });
    }

    setSaving(true);
    try {
      const payload = {
        name:      form.name,
        last_name: form.last_name,
        ...(telefonoCambio ? { phone: form.phone } : {}),
      };
      if (showPwd && form.password) {
        payload.current_password = form.current_password;
        payload.password         = form.password;
      }

      const { data } = await authService.updateProfile(payload);

      // Refrescar estado global y localStorage
      const updatedUser = { ...user, name: data.user.name, last_name: data.user.last_name, phone: data.user.phone };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Actualizar form con datos frescos
      setForm(f => ({ ...f,
        name:             data.user.name      || '',
        last_name:        data.user.last_name || '',
        phone:            telefonoGuardado(data.user.phone),
        current_password: '',
        password:         '',
        confirm_password: '',
      }));
      setShowPwd(false);
      setDirty(false);
      setAlert({ type: 'success', message: '✓ Perfil actualizado correctamente' });
    } catch (err) {
      // D2: el servidor exige el código verificado para cambiar la clave; si
      // venció mientras llenaba el formulario, se vuelve al paso del código.
      if (err.response?.data?.code === 'OTP_NOT_VERIFIED') {
        setOtpVerified(false); setOtpSent(false); setOtpValue('');
      }
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Error al actualizar' });
    } finally {
      setSaving(false);
    }
  };

  return {
    form, alert, setAlert, saving, showPwd, otpVerified, otpSent, otpValue, setOtpValue, otpSending,
    otpLoading, otpError, countdown, user, role, isOwner, sendOtp, verifyOtp, handleTogglePwd, set,
    handleSubmit,
  };
}
